/* global clearInterval, console, CustomFunctions, setInterval */
import { AccountDCCode, AccountName, DatatypeName, CurrencyRate, UnitCurrency, UnitName } from "./ApiCalls";
import { _pushOperation } from "./BatchCustomFunctionCalls";
import { _extraDataPushOperation } from "./BatchCustomFunctionCallsDRExtraData";
import { DRLog } from "./util/Logging";
import { getDRErrorValue } from "./util/StateManagement";
import { isValidString, isValidNumber, isValidBoolean } from "./util/ValidationHelpers";
/**
 * Get default balance of an account (debit: 1, credit: -1)
 * @customfunction DRACCOUNTDCCODE
 * @param Account_No Account ID Number.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 * @returns Balance of an account (debit: 1, credit: -1)
 * @helpurl https://clausion-docs.insightsoftware.com/hc/en-us
 */
export async function DRAccountDCCode(Account_No: string, invocation?: CustomFunctions.Invocation): Promise<string> {
  try {
    if (!isValidString(Account_No)) {
      return isValidString(getDRErrorValue()) ? getDRErrorValue() : CustomFunctions.ErrorCode.invalidValue;
    }
    const addressComponents = invocation.address.split("!");
    await DRLog("DEBUG", `DRACCOUNTDCCODE(${Account_No})`, addressComponents);
    const response = await AccountDCCode(Account_No.toUpperCase());
    return response;
  } catch (error) {
    await DRLog("ERROR", `Cell ${invocation.address} has a #VALUE! error in custom function.`, error);
    return CustomFunctions.ErrorCode.invalidValue;
  }
}

/**
 * Get account name
 * @customfunction DRACCOUNTNAME
 * @param Account_No Account ID Number.
 * @param Language_Code Language Code (e.g. "FI")
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress 
 * @returns Account name
 * @helpurl https://clausion-docs.insightsoftware.com/hc/en-us
 */
export async function DRAccountName(
  Account_No: string,
  Language_Code: string,
  invocation?: CustomFunctions.Invocation
): Promise<string> {
  try {
    if (!isValidString(Account_No) || !isValidString(Language_Code)) {
      return isValidString(getDRErrorValue()) ? getDRErrorValue() : CustomFunctions.ErrorCode.invalidValue;
    }
    const addressComponents = invocation.address.split("!");
    await DRLog("DEBUG", `DRACCOUNTNAME(${Account_No}, ${Language_Code})`, addressComponents);
    const response = await AccountName(Account_No.toUpperCase(), Language_Code.toUpperCase());
    return response;
  } catch (error) {
    await DRLog("ERROR", `Cell ${invocation.address} has a #VALUE! error in custom function.`, error);
    return CustomFunctions.ErrorCode.invalidValue;
  }
}

/**
 * Writes a message to console.log().
 * @customfunction DRDATATYPENAME
 * @param Datatype_Code Datatype ID
 * @param Language_Code Language Code (e.g. "FI")
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 * @returns Datatype name
 * @helpurl https://clausion-docs.insightsoftware.com/hc/en-us
 */
export async function DRDataTypeName(
  Datatype_Code: string,
  Language_Code: string,
  invocation?: CustomFunctions.Invocation
): Promise<string> {
  try {
    if (!isValidString(Datatype_Code) || !isValidString(Language_Code)) {
      return isValidString(getDRErrorValue()) ? getDRErrorValue() : CustomFunctions.ErrorCode.invalidValue;
    }
    const addressComponents = invocation.address.split("!");
    await DRLog("DEBUG", `DRDATATYPENAME(${(Datatype_Code.toUpperCase(), Language_Code.toUpperCase())})`, addressComponents);
    const response = await DatatypeName(Datatype_Code.toUpperCase(), Language_Code.toUpperCase());
    return response;
  } catch (error) {
    await DRLog("ERROR", `Cell ${invocation.address} has a #VALUE! error in custom function.`, error);
    return CustomFunctions.ErrorCode.invalidValue;
  }
}

/**
 * Get currency rate
 * @customfunction DRCURRENCYRATE
 * @param Currency Currency ID
 * @param Rate_Type INCOME, BALANCE or OPENING
 * @param Year Year (e.g. 2021, 2020, ...)
 * @param Period Period (e.g. 0, 1, 2, ...)
 * @param Datatype_Code Datatype ID
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 * @returns Currency rate
 * @helpurl https://clausion-docs.insightsoftware.com/hc/en-us
 */
export async function DRCurrencyRate(
  Currency: string,
  Rate_Type: 10 | 20 | 30,
  Datatype_Code: string,
  Year: number,
  Period: number,
  invocation?: CustomFunctions.Invocation
): Promise<string> {
  try {
    if (
      !isValidString(Currency) ||
      !Rate_Type ||
      !isValidString(Datatype_Code) ||
      !isValidNumber(Year) ||
      !isValidNumber(Period)
    ) {
      return isValidString(getDRErrorValue()) ? getDRErrorValue() : CustomFunctions.ErrorCode.invalidValue;
    }
    const addressComponents = invocation.address.split("!");
    await DRLog(
      "DEBUG",
      `DRCURRENCYRATE(${Currency.toUpperCase()}, ${Rate_Type}, ${Datatype_Code.toUpperCase()}, ${Year}, ${Period})`,
      addressComponents
    );
    const response = await CurrencyRate(Currency.toUpperCase(), Rate_Type, Datatype_Code.toUpperCase(), Year, Period);
    return response;
  } catch (error) {
    await DRLog("ERROR", `Cell ${invocation.address} has a #VALUE! error in custom function.`, error);
    return CustomFunctions.ErrorCode.invalidValue;
  }
}

/**
 * Get unit currency code (ID)
 * @customfunction DRUNITCURRENCY
 * @param Dim_Id Dimension (Typically DIM00, DIM01, ...)
 * @param Unit Organization-specific unit code
 * @param Year Year (e.g. 2021, 2020, ...)
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 * @returns Unit currency code (ID)
 * @helpurl https://clausion-docs.insightsoftware.com/hc/en-us
 */
export async function DRUnitCurrency(
  Dim_Id: string,
  Unit: string,
  Year: number,
  invocation?: CustomFunctions.Invocation
): Promise<string> {
  try {
    if (!isValidString(Dim_Id) || !isValidString(Unit) || !isValidNumber(Year)) {
      return isValidString(getDRErrorValue()) ? getDRErrorValue() : CustomFunctions.ErrorCode.invalidValue;
    }
    const addressComponents = invocation.address.split("!");
    await DRLog("DEBUG", `DRUNITCURRENCY(${Dim_Id.toUpperCase()}, ${Unit.toUpperCase()}, ${Year})`, addressComponents);
    const response = await UnitCurrency(Dim_Id.toUpperCase(), Unit.toUpperCase(), Year);
    return response;
  } catch (error) {
    await DRLog("ERROR", `Cell ${invocation.address} has a #VALUE! error in custom function.`, error);
    return CustomFunctions.ErrorCode.invalidValue;
  }
}

/**
 * Get currency rate
 * @customfunction DRUNITNAME
 * @param Dim_Id Dimension (Typically DIM00, DIM01, ...)
 * @param Unit Organization-specific unit code
 * @param Year Year (e.g. 2021, 2020, ...)
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 * @returns Currency rate
 * @helpurl https://clausion-docs.insightsoftware.com/hc/en-us
 */
export async function DRUnitName(
  Dim_Id: string,
  Unit: string,
  Year: number,
  Language_Code: string,
  invocation?: CustomFunctions.Invocation
): Promise<string> {
  try {
    if (!isValidString(Dim_Id) || !isValidString(Unit) || !isValidNumber(Year) || !isValidString(Language_Code)) {
      return isValidString(getDRErrorValue()) ? getDRErrorValue() : CustomFunctions.ErrorCode.invalidValue;
    }
    const addressComponents = invocation.address.split("!");
    await DRLog(
      "DEBUG",
      `DRUNITNAME(${Dim_Id.toUpperCase()}, ${Unit.toUpperCase()}, ${Year}, ${Language_Code.toUpperCase()})`,
      addressComponents
    );
    const response = await UnitName(Dim_Id.toUpperCase(), Unit.toUpperCase(), Year, Language_Code.toUpperCase());
    return response;
  } catch (error) {
    await DRLog("ERROR", `Cell ${invocation.address} has a #VALUE! error in custom function.`, error);
    return CustomFunctions.ErrorCode.invalidValue;
  }
}

/**
 * Get data (debit: positive, credit: negative)
 * @customfunction DRDATA
 * @param Datatype_Code Typically ACT, BUD, EST, ...
 * @param Doc_Serie 10000, 20000, 60002, ...
 * @param Account_No Account ID Number.
 * @param Year Year (e.g. 2021, 2020, ...)
 * @param Period Period (e.g. 0, 1, 2, ...)
 * @param Cumulative Is it cumulative: TRUE or FALSE
 * @param Currency Currency ID
 * @param D0_Unit Organization-specific unit codes
 * @param D0_CoUnit Organization-specific unit codes. Optional. This argument only for DIM00. When empty, takes value from argument 8.
 * @param D0_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D1_Unit Organization-specific unit codes
 * @param D1_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D2_Unit Organization-specific unit codes
 * @param D2_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D3_Unit Organization-specific unit codes
 * @param D3_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D4_Unit Organization-specific unit codes
 * @param D4_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D5_Unit Organization-specific unit codes
 * @param D5_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D6_Unit Organization-specific unit codes
 * @param D6_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D7_Unit Organization-specific unit codes
 * @param D7_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D8_Unit Organization-specific unit codes
 * @param D8_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D9_Unit Organization-specific unit codes
 * @param D9_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D0_EF This field will filter data based on Dimension extra field For dimension 0. specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D1_EF This field will filter data based on Dimension extra field For dimension 1: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D2_EF This field will filter data based on Dimension extra field For dimension 2: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D3_EF This field will filter data based on Dimension extra field For dimension 3: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D4_EF This field will filter data based on Dimension extra field For dimension 4: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D5_EF This field will filter data based on Dimension extra field For dimension 5: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D6_EF This field will filter data based on Dimension extra field For dimension 6: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D7_EF This field will filter data based on Dimension extra field For dimension 7: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D8_EF This field will filter data based on Dimension extra field For dimension 8: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param D9_EF This field will filter data based on Dimension extra field For dimension 9: specify Extra Field ID (e.g., E01) then the sign (>, <, =,!=,<=,>=,BT,!BT,CT,IN,!IN), followed by the value Example1: E01=apples|E02>{10,550} Example2: E01>10000|E02BT25;50.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 * @returns Account balance
 * @helpurl https://clausion-docs.insightsoftware.com/hc/en-us
 */
export async function DRData(
  Datatype_Code: string,
  Doc_Serie: string,
  Account_No: string,
  Year: number,
  Period: string,
  Cumulative: boolean,
  Currency: string,
  D0_Unit: string,
  D0_CoUnit?: string,
  D0_Hier?: string,
  D1_Unit?: string,
  D1_Hier?: string,
  D2_Unit?: string,
  D2_Hier?: string,
  D3_Unit?: string,
  D3_Hier?: string,
  D4_Unit?: string,
  D4_Hier?: string,
  D5_Unit?: string,
  D5_Hier?: string,
  D6_Unit?: string,
  D6_Hier?: string,
  D7_Unit?: string,
  D7_Hier?: string,
  D8_Unit?: string,
  D8_Hier?: string,
  D9_Unit?: string,
  D9_Hier?: string,
  D0_EF?: string,
  D1_EF?: string,
  D2_EF?: string,
  D3_EF?: string,
  D4_EF?: string,
  D5_EF?: string,
  D6_EF?: string,
  D7_EF?: string,
  D8_EF?: string,
  D9_EF?: string,
  invocation?: CustomFunctions.Invocation
): Promise<string> {
  try {
    if (
      !isValidString(Datatype_Code) ||
      !isValidString(Doc_Serie) ||
      !isValidString(Account_No) ||
      !isValidNumber(Year) ||
      !isValidString(Period) ||
      !isValidBoolean(Cumulative) ||
      !isValidString(Currency) ||
      !isValidString(D0_Unit)
    ) {
      return isValidString(getDRErrorValue()) ? getDRErrorValue() : CustomFunctions.ErrorCode.invalidValue;
    }
    const parameterArray = [
      Datatype_Code.toUpperCase(),
      Doc_Serie.toUpperCase(),
      Account_No.toUpperCase(),
      Year,
      Period.toUpperCase(),
      Cumulative,
      Currency.toUpperCase(),
      D0_Unit.toUpperCase(),
      D0_CoUnit?.toUpperCase(),
      D0_Hier?.toUpperCase(),
      D1_Unit?.toUpperCase(),
      D1_Hier?.toUpperCase(),
      D2_Unit?.toUpperCase(),
      D2_Hier?.toUpperCase(),
      D3_Unit?.toUpperCase(),
      D3_Hier?.toUpperCase(),
      D4_Unit?.toUpperCase(),
      D4_Hier?.toUpperCase(),
      D5_Unit?.toUpperCase(),
      D5_Hier?.toUpperCase(),
      D6_Unit?.toUpperCase(),
      D6_Hier?.toUpperCase(),
      D7_Unit?.toUpperCase(),
      D7_Hier?.toUpperCase(),
      D8_Unit?.toUpperCase(),
      D8_Hier?.toUpperCase(),
      D9_Unit?.toUpperCase(),
      D9_Hier?.toUpperCase(),
      D0_EF?.toUpperCase(),
      D1_EF?.toUpperCase(),
      D2_EF?.toUpperCase(),
      D3_EF?.toUpperCase(),
      D4_EF?.toUpperCase(),
      D5_EF?.toUpperCase(),
      D6_EF?.toUpperCase(),
      D7_EF?.toUpperCase(),
      D8_EF?.toUpperCase(),
      D9_EF?.toUpperCase(),
    ];
    const addressComponents = invocation.address.split("!");
    const logStringAddress = `Sheet: ${addressComponents[0]}, Cell: ${addressComponents[1]}`;
    const response = await _pushOperation("DRData", parameterArray, logStringAddress);
    return response;
  } catch (error) {
    await DRLog("ERROR", `Cell ${invocation.address} has a #VALUE! error in custom function.`, error);
    return CustomFunctions.ErrorCode.invalidValue;
  }
}

/**
 * Get DataType Extra Field value
 * @customfunction DREXTRADATA
 * @param Datatype_Code Typically ACT, BUD, EST, ...
 * @param Doc_Serie 10000, 20000, 60002, ...
 * @param Account_No Account ID Number.
 * @param Year Year (e.g. 2021, 2020, ...)
 * @param ExtraField_Id DataType ExtraField (e.g. "E01", "E02", ...)
 * @param D0_Unit Organization-specific unit codes
 * @param D0_CoUnit Organization-specific unit codes. Optional. This argument only for DIM00. When empty, takes value from argument 8.
 * @param D0_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D1_Unit Organization-specific unit codes
 * @param D1_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D2_Unit Organization-specific unit codes
 * @param D2_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D3_Unit Organization-specific unit codes
 * @param D3_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D4_Unit Organization-specific unit codes
 * @param D4_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D5_Unit Organization-specific unit codes
 * @param D5_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D6_Unit Organization-specific unit codes
 * @param D6_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D7_Unit Organization-specific unit codes
 * @param D7_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D8_Unit Organization-specific unit codes
 * @param D8_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param D9_Unit Organization-specific unit codes
 * @param D9_Hier Could be, for example, "STRUCTURE1" Optional. Hierarchy used for consolidation units. When empty, the first hierarchy is used.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 * @returns Account balance
 * @helpurl https://clausion-docs.insightsoftware.com/hc/en-us
 */
export async function DRExtraData(
  Datatype_Code: string,
  Doc_Serie: string,
  Account_No: string,
  Year: number,
  ExtraField_Id: string,
  D0_Unit: string,
  D0_CoUnit?: string,
  D0_Hier?: string,
  D1_Unit?: string,
  D1_Hier?: string,
  D2_Unit?: string,
  D2_Hier?: string,
  D3_Unit?: string,
  D3_Hier?: string,
  D4_Unit?: string,
  D4_Hier?: string,
  D5_Unit?: string,
  D5_Hier?: string,
  D6_Unit?: string,
  D6_Hier?: string,
  D7_Unit?: string,
  D7_Hier?: string,
  D8_Unit?: string,
  D8_Hier?: string,
  D9_Unit?: string,
  D9_Hier?: string,
  invocation?: CustomFunctions.Invocation
): Promise<string> {
  try {
    if (
      !isValidString(Datatype_Code) ||
      !isValidString(Doc_Serie) ||
      !isValidString(Account_No) ||
      !isValidNumber(Year) ||
      !isValidString(ExtraField_Id) ||
      !isValidString(D0_Unit)
    ) {
      return isValidString(getDRErrorValue()) ? getDRErrorValue() : CustomFunctions.ErrorCode.invalidValue;
    }
    const parameterArray = [
      Datatype_Code.toUpperCase(),
      Doc_Serie.toUpperCase(),
      Account_No.toUpperCase(),
      Year,
      ExtraField_Id,
      D0_Unit?.toUpperCase(),
      D0_CoUnit?.toUpperCase(),
      D0_Hier?.toUpperCase(),
      D1_Unit?.toUpperCase(),
      D1_Hier?.toUpperCase(),
      D2_Unit?.toUpperCase(),
      D2_Hier?.toUpperCase(),
      D3_Unit?.toUpperCase(),
      D3_Hier?.toUpperCase(),
      D4_Unit?.toUpperCase(),
      D4_Hier?.toUpperCase(),
      D5_Unit?.toUpperCase(),
      D5_Hier?.toUpperCase(),
      D6_Unit?.toUpperCase(),
      D6_Hier?.toUpperCase(),
      D7_Unit?.toUpperCase(),
      D7_Hier?.toUpperCase(),
      D8_Unit?.toUpperCase(),
      D8_Hier?.toUpperCase(),
      D9_Unit?.toUpperCase(),
      D9_Hier?.toUpperCase(),
    ];
    const addressComponents = invocation.address.split("!");
    const logStringAddress = `Sheet: ${addressComponents[0]}, Cell: ${addressComponents[1]}`;
    const response = await _extraDataPushOperation("DRExtraData", parameterArray, logStringAddress);
    return response;
  } catch (error) {
    await DRLog("ERROR", `Cell ${invocation.address} has a #VALUE! error in custom function.`, error);
    return CustomFunctions.ErrorCode.invalidValue;
  }
}

CustomFunctions.associate("DRACCOUNTDCCODE", DRAccountDCCode);
CustomFunctions.associate("DRACCOUNTNAME", DRAccountName);
CustomFunctions.associate("DRDATATYPENAME", DRDataTypeName);
CustomFunctions.associate("DRCURRENCYRATE", DRCurrencyRate);
CustomFunctions.associate("DRUNITCURRENCY", DRUnitCurrency);
CustomFunctions.associate("DRUNITNAME", DRUnitName);
CustomFunctions.associate("DRDATA", DRData);
CustomFunctions.associate("DREXTRADATA", DRExtraData);